import * as React from 'react';

import { ModalFullscreen } from '@cian/ui-kit';

import type { IAiSummaryResponse } from '../AiSummaryModalDesktop/types';
import { ELoadingStatus } from '../../types/redux/loadingStatus';
import { ErrorMobileScreen } from './ErrorMobileScreen';
import { LoadingScreen } from './LoadingScreen';
import { Header } from './parts/Header';
import { MobileContent } from './parts/MobileContent';
import { MobileNavbar } from './parts/MobileNavbar';
import * as style from './styles.css';

type TVisualState = 'loading' | 'ready';

export interface IAiSummaryMobileActions {
  newbuildingId: number;
  call?: React.ReactNode;
  chat?: React.ReactNode;
  onShowPhone?(): void;
}

export interface IAiSummaryModalMobileProps {
  open: boolean;
  onClose: () => void;
  summary: IAiSummaryResponse | null;
  status: ELoadingStatus;
  requestId: number;
  onReload: () => void;
  actions: IAiSummaryMobileActions[];
  onTabChange?: (newbuildingId: number) => void;
  onReportError?: (newbuildingId: number) => void;
  onCardClick?: (newbuildingId: number) => void;
}

export const AiSummaryModalMobile = ({
  open,
  onClose,
  summary,
  status,
  requestId,
  onReload,
  actions,
  onTabChange,
  onReportError,
  onCardClick,
}: IAiSummaryModalMobileProps): React.JSX.Element | null => {
  const [activeNewbuildingId, setActiveNewbuildingId] = React.useState<number | null>(null);
  const [completedRequestId, setCompletedRequestId] = React.useState<number | null>(() =>
    status === ELoadingStatus.Initial || status === ELoadingStatus.Loading ? null : requestId,
  );
  const [visualStateRequestId, setVisualStateRequestId] = React.useState(requestId);
  const [visualState, setVisualState] = React.useState<TVisualState>(() =>
    status === ELoadingStatus.Succeed ? 'ready' : 'loading',
  );
  const [isContentRevealing, setIsContentRevealing] = React.useState(() => status === ELoadingStatus.Succeed);
  const [contentRevealKey, setContentRevealKey] = React.useState(0);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const isLoading =
    status === ELoadingStatus.Initial || status === ELoadingStatus.Loading || completedRequestId !== requestId;
  const activeSummary = summary?.summarization.find(item => item.id === activeNewbuildingId);
  const activeActions = actions.find(item => item.newbuildingId === activeNewbuildingId);

  React.useEffect(() => {
    const firstNewbuildingId = summary?.summarization[0]?.id;

    if (firstNewbuildingId && !summary.summarization.some(item => item.id === activeNewbuildingId)) {
      setActiveNewbuildingId(firstNewbuildingId);
    }
  }, [activeNewbuildingId, summary]);

  const handleLoadingComplete = React.useCallback(() => setCompletedRequestId(requestId), [requestId]);

  React.useEffect(() => {
    if (requestId !== visualStateRequestId) {
      setVisualStateRequestId(requestId);
      setVisualState('loading');
      setIsContentRevealing(false);

      return;
    }

    if (status === ELoadingStatus.Succeed && completedRequestId === requestId && visualState === 'loading') {
      setVisualState('ready');
      setIsContentRevealing(true);
      setContentRevealKey(key => key + 1);
    }
  }, [completedRequestId, requestId, status, visualStateRequestId, visualState]);

  const handleContentRevealComplete = React.useCallback(() => setIsContentRevealing(false), []);

  const handleSelectNewbuilding = React.useCallback(
    (newbuildingId: number) => {
      if (newbuildingId === activeNewbuildingId) {
        return;
      }

      setActiveNewbuildingId(newbuildingId);
      onTabChange?.(newbuildingId);
      contentRef.current?.scrollTo?.({ behavior: 'auto', top: 0 });
      setIsContentRevealing(true);
      setContentRevealKey(key => key + 1);
    },
    [activeNewbuildingId, onTabChange],
  );

  if (status === ELoadingStatus.Failed) {
    return (
      <ModalFullscreen onClose={onClose} open={open}>
        <div className={style['state-container']}>
          <Header onClose={onClose} />
          <ErrorMobileScreen onReload={onReload} />
        </div>
      </ModalFullscreen>
    );
  }

  if (isLoading || visualState === 'loading') {
    return (
      <ModalFullscreen onClose={onClose} open={open}>
        <div className={style['state-container']}>
          <Header onClose={onClose} />
          <LoadingScreen key={requestId} onComplete={handleLoadingComplete} />
        </div>
      </ModalFullscreen>
    );
  }

  if (!summary || !activeSummary) {
    return null;
  }

  const resultContent = (
    <div className={style['container']}>
      <div className={style['top-panel']}>
        <Header onClose={onClose} />
        <MobileNavbar
          activeNewbuildingId={activeSummary.id}
          items={summary.summarization}
          onSelectNewbuilding={handleSelectNewbuilding}
        />
      </div>
      <div className={style['content']} data-testid="ai-summary-mobile-result-card" ref={contentRef}>
        <MobileContent
          call={activeActions?.call}
          chat={activeActions?.chat}
          disclaimer={summary.disclaimer}
          emptyState={summary.emptyState}
          isRevealing={isContentRevealing}
          onRevealComplete={handleContentRevealComplete}
          onReportError={onReportError}
          onCardClick={onCardClick}
          onShowPhone={activeActions?.onShowPhone}
          revealKey={contentRevealKey}
          summary={activeSummary}
        />
      </div>
    </div>
  );

  return (
    <ModalFullscreen onClose={onClose} open={open}>
      {resultContent}
    </ModalFullscreen>
  );
};
